import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, SimpleGrid } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedCallback } from "@mantine/hooks";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Button,
  DECIMAL_PROPS,
  MonthPicker,
  NumberInput,
  Select,
  YesNoRadioButton,
} from "components";
import SectionTitle, {
  SectionTitleIndicatorType,
} from "pages/clients/view/components/section-title";
import { CreditOptionsResponse, CreditResponse, IncomeType } from "types";
import { AppUtils } from "utils";

interface CreditRequestIncomeProps {
  credit: CreditResponse;
  form: any;
  options: CreditOptionsResponse;
  partner: boolean;
  readOnly?: boolean;
  isBankCredit?: boolean;
  hasWarning?: boolean;
  onWarningClick?: () => void;
  indicatorType?: SectionTitleIndicatorType;
  syncValues?: IncomeObject[];
  syncVersion?: number;
}

interface IncomeObject {
  creditRequestId?: number;
  incomeType?: IncomeType;
  startDate?: Date;
  amount?: number;
  partner?: boolean;
}
const tempIncomeObject = {
  creditRequestId: undefined,
  incomeType: undefined,
  startDate: undefined,
  amount: undefined,
  partner: false,
} as IncomeObject;

const propertyName = "incomes";

const CreditRequestIncome = ({
  credit,
  form,
  options,
  partner,
  readOnly = false,
  isBankCredit = false,
  hasWarning = false,
  onWarningClick,
  indicatorType = "warning",
  syncValues,
  syncVersion = 0,
}: CreditRequestIncomeProps) => {
  const { t } = useTranslation();
  const [visibleFields, setVisibleFields] = useState<string[]>(
    AppUtils.getVisibleIncomeFields(credit[propertyName]),
  );

  const sectionLabel = useMemo(
    () => (partner ? t("credit.income.partner.title") : t("credit.income.title")),
    [partner, t],
  );
  const additionalIncomeLabel = useMemo(
    () =>
      partner
        ? t("credit.income.hasAdditionalIncome.labelPartner")
        : t("credit.income.hasAdditionalIncome.label"),
    [partner, t],
  );
  const [hasAdditionalIncome, setHasAdditionalIncome] = useState(() =>
    (credit[propertyName] || []).some((income) => income.partner === partner),
  );

  const getInitialData = useCallback(() => {
    const formValues: IncomeObject[] | undefined = credit[propertyName];
    let array = [];
    if (!formValues || formValues.length === 0) {
      if (isBankCredit) {
        array.push(tempIncomeObject);
      }
    } else {
      const filtered = formValues.filter((el) => el.partner === partner);
      if (filtered.length > 0) {
        array = filtered.map((el) => ({
          ...el,
          startDate: el.startDate ? new Date(el.startDate) : undefined,
        }));
      } else {
        if (isBankCredit) {
          array.push(tempIncomeObject);
        }
      }
    }
    return array;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBankCredit, partner]);

  const incomeForm = useForm({
    mode: "uncontrolled",
    initialValues: getInitialData() as IncomeObject[],
    onValuesChange(values, previous) {
      updateCredit(values, previous);
    },
  });

  useEffect(() => {
    // Keep the values loaded from `credit` on the initial render. A synchronization
    // is only needed when the parent explicitly advances its sync version.
    if (syncVersion === 0) {
      return;
    }

    const nextValues =
      syncValues && syncValues.length > 0
        ? syncValues.map((el) => ({
            ...el,
            startDate: el.startDate ? new Date(el.startDate) : undefined,
          }))
        : [{ ...tempIncomeObject }];

    incomeForm.setInitialValues(nextValues);
    incomeForm.setValues(nextValues);
    setVisibleFields(AppUtils.getVisibleIncomeFields(syncValues ?? []));
    if (!isBankCredit) {
      setHasAdditionalIncome((syncValues ?? []).some((income) => income.partner === partner));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncVersion]);

  const updateCredit = useDebouncedCallback(async (values: any, previous: any) => {
    const prevVisibleFields = AppUtils.getVisibleIncomeFields(previous);
    const newVisibleFields = AppUtils.getVisibleIncomeFields(values);
    AppUtils.clearArrayFieldsWhenUpdating(values, previous, newVisibleFields);
    AppUtils.updateArrayFieldsWhenVisibilityChanges(
      prevVisibleFields,
      newVisibleFields,
      values,
      incomeForm,
    );

    const formValues: IncomeObject[] | undefined = form.getValues()[propertyName];
    const tempArray: IncomeObject[] = formValues
      ? formValues.filter((el: IncomeObject) => el.partner !== partner)
      : [];
    Object.keys(values).forEach((key) => {
      const item: IncomeObject = { ...values[key] };
      const isNotEmpty = item.incomeType || item.startDate || item.amount;
      if (isNotEmpty) {
        item.creditRequestId = credit.id;
        item.partner = partner;
        tempArray.push(item);
      }
    });
    form.setFieldValue(propertyName, tempArray);
    setVisibleFields(newVisibleFields);
  }, 300);

  const transformed = incomeForm.getTransformedValues();
  const length = Object.keys(transformed).length;
  const showIncomeFields = isBankCredit || hasAdditionalIncome;

  const handleAdditionalIncomeChange = (hasIncome: boolean) => {
    setHasAdditionalIncome(hasIncome);

    if (hasIncome) {
      if (incomeForm.getValues().length === 0) {
        incomeForm.setInitialValues([{ ...tempIncomeObject }]);
        incomeForm.setValues([{ ...tempIncomeObject }]);
      }
      return;
    }

    incomeForm.setInitialValues([]);
    incomeForm.setValues([]);
    form.setFieldValue(
      propertyName,
      (form.getValues()[propertyName] || []).filter(
        (income: IncomeObject) => income.partner !== partner,
      ),
    );
  };
  return (
    <Box>
      <SectionTitle
        label={sectionLabel}
        isBankCredit={isBankCredit}
        hasWarning={hasWarning}
        onWarningClick={onWarningClick}
        indicatorType={indicatorType}
      />
      {!isBankCredit && (
        <Box mt="md">
          <YesNoRadioButton
            label={additionalIncomeLabel}
            name={partner ? "hasPartnerAdditionalIncome" : "hasAdditionalIncome"}
            checked={hasAdditionalIncome}
            onChange={handleAdditionalIncomeChange}
            disabled={readOnly}
          />
        </Box>
      )}
      {showIncomeFields && (
        <Box pt={"xs"} mt={"sm"}>
          {Object.keys(transformed).map((incomeKey, index: number) => (
            <Box key={`${incomeKey}-${index}`} mt={index > 0 ? "md" : undefined}>
              <SimpleGrid cols={4} mt={"sm"}>
                <Select
                  label={t(`credit.income.incomeType.label`)}
                  data={options["incomeType"]
                    .filter(
                      (el) => form.values["maritalStatus"] !== "MARRIED" || el !== "WIDOWS_PENSION",
                    )
                    .map((el) => ({
                      value: el,
                      label: t(`credit.income.incomeType.options.${el}`),
                    }))}
                  key={incomeForm.key(`${index}.incomeType`)}
                  {...incomeForm.getInputProps(`${index}.incomeType`)}
                  clearable={true}
                  disabled={readOnly}
                />
                <NumberInput
                  label={t(`credit.income.amount.label`)}
                  key={incomeForm.key(`${index}.amount`)}
                  {...incomeForm.getInputProps(`${index}.amount`)}
                  min={0}
                  {...DECIMAL_PROPS}
                  disabled={readOnly}
                />
                {visibleFields.includes(`incomes.${index}.startDate`) && (
                  <MonthPicker
                    label={t(`credit.income.startDate.label`)}
                    key={incomeForm.key(`${index}.startDate`)}
                    {...incomeForm.getInputProps(`${index}.startDate`)}
                    maxDate={new Date()}
                    disabled={readOnly}
                  />
                )}
                {length > 1 && !readOnly && (
                  <Button
                    variant="transparent"
                    size="compact-sm"
                    color="var(--mantine-color-error)"
                    mt={14}
                    style={{ alignSelf: "center" }}
                    onClick={() => AppUtils.removeObject(incomeForm, index, undefined)}
                  >
                    <IconTrash size={20} />
                  </Button>
                )}
              </SimpleGrid>
            </Box>
          ))}
          {!readOnly && (
            <Button
              onClick={() => AppUtils.addNewObject(incomeForm, tempIncomeObject)}
              size="compact-xs"
              variant="transparent"
              mt="md"
            >
              <>
                <Box mr={4}>
                  <IconPlus size={16} />
                </Box>
                {t("credit.income.add")}
              </>
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CreditRequestIncome;
